/**
 * buy-buttons-atc.js
 *
 * Handles ATC button price display and Recharge widget integration.
 * Loaded via <script defer> from buy-buttons.liquid.
 *
 * Section ID is read from data-section-id on the button element itself,
 * avoiding the document.currentScript === null problem that occurs with
 * deferred scripts.
 *
 * Supports multiple product forms on the same page (quick-add, etc.)
 * by initializing every unvisited [data-section-id] button found in the DOM.
 */

(function () {
  'use strict';

  // ── Find every ATC button that hasn't been initialized yet ───────────────

  var buttons = document.querySelectorAll(
    '.product-form__submit.button--atc-custom[data-section-id]'
  );

  if (!buttons.length) return;

  buttons.forEach(function (btn) {
    // Guard against double-binding when the script tag appears more than once
    if (btn.dataset.atcInit === '1') return;
    btn.dataset.atcInit = '1';

    initButton(btn);
  });

  // ── Per-button init ───────────────────────────────────────────────────────

  function initButton(btn) {
    var SECTION_ID  = btn.dataset.sectionId;
    var priceEl     = btn.querySelector('[data-atc-price]');
    var labelEl     = btn.querySelector('.atc-label__text');
    var priceWrapEl = btn.querySelector('.atc-price-wrap');

    if (!priceEl) return;

    // Tracks the last known variant price in cents so onRCMutation can fall
    // back to it when Recharge temporarily clears its price during recalculation.
    var lastVariantPriceCents = null;

    // ── Helpers ────────────────────────────────────────────────────────────

    function formatMoney(cents) {
      return (cents / 100).toLocaleString(undefined, {
        style: 'currency',
        currency:
          (window.Shopify &&
            window.Shopify.currency &&
            window.Shopify.currency.active) ||
          'USD',
      });
    }

    /**
     * Animate the price element to the new text value.
     * Compares against a pending value (not DOM text) so the 120 ms animation
     * delay does not cause the guard to silently drop a valid RC price update.
     */
    var pendingPrice = null;

    function setPrice(text) {
      var clean = (text || '').toString().trim();
      if (!clean || pendingPrice === clean) return;

      pendingPrice = clean;
      priceEl.classList.add('is-changing');
      setTimeout(function () {
        priceEl.textContent = clean;
        priceEl.classList.remove('is-changing');
      }, 120);
    }

    function showPrice(show) {
      if (!priceWrapEl) return;
      if (show) {
        priceWrapEl.setAttribute('data-show-price', '');
        // Only actually display if the page has fully loaded.
        if (document.readyState === 'complete') {
          priceWrapEl.style.display = '';
        }
      } else {
        priceWrapEl.removeAttribute('data-show-price');
        priceWrapEl.style.display = 'none';
      }
    }

    /**
     * Update button label text and price visibility.
     * Does NOT touch btn.disabled — that is owned by:
     *   • Liquid template  — initial page render
     *   • product-form.js  — during/after form submission (onSubmitHandler)
     *   • product-info.js  — after variant change (toggleSubmitButton)
     */
    function updateButtonDisplay(soldOut) {
      if (labelEl) {
        labelEl.textContent = soldOut
          ? (window.variantStrings && window.variantStrings.soldOut) ||
            'Sold Out'
          : (window.variantStrings && window.variantStrings.addToCart) ||
            'Add to Cart';
      }
      showPrice(!soldOut);
    }

    // ── Recharge widget helpers ────────────────────────────────────────────

    /** Returns the price string from the active Recharge radio, or null. */
    function getRCPrice() {
      var el = document.querySelector(
        '.rc-widget .rc-radio--active .price-label'
      );
      if (!el) return null;
      var text = el.textContent.trim();
      return text.length ? text : null;
    }

    // ── Recharge observer (debounced) ──────────────────────────────────────

    var rcDebounce = null;

    function onRCMutation() {
      clearTimeout(rcDebounce);
      rcDebounce = setTimeout(function () {
        var rcPrice = getRCPrice();
        if (rcPrice) {
          // RC widget has a live price — use it (covers subscription ↔ one-time
          // switches and delivery-frequency changes).
          showPrice(true);
          setPrice(rcPrice);
        } else if (lastVariantPriceCents !== null) {
          // RC widget is mid-recalculation and temporarily shows no price.
          // Fall back to the variant base price so the button never goes blank.
          showPrice(true);
          setPrice(formatMoney(lastVariantPriceCents));
        }
      }, 80);
    }

    /**
     * Attach a MutationObserver to the RC widget and read the initial price.
     * Extracted so it can be called both when the widget is already in the DOM
     * and when the body observer detects it appearing later.
     */
    function setupRCObserver(rcWidget) {
      var initial = getRCPrice();
      if (initial) {
        showPrice(true);
        setPrice(initial);
      }
      new MutationObserver(onRCMutation).observe(rcWidget, {
        subtree: true,
        childList: true,
        attributes: true,
        characterData: true,
        attributeFilter: ['class'],
      });
    }

    /**
     * Watch for the RC widget via a MutationObserver on document.body instead
     * of a timed poll. On throttled mobile connections Recharge (a large Vue
     * app) can take 5–10 s to bootstrap — longer than any reasonable poll
     * window. A body observer fires the instant .rc-widget is inserted.
     */
    function watchRCWidget() {
      var rcWidget = document.querySelector('.rc-widget');
      if (rcWidget) {
        setupRCObserver(rcWidget);
        return;
      }

      var bodyObserver = new MutationObserver(function (mutations, obs) {
        var widget = document.querySelector('.rc-widget');
        if (widget) {
          obs.disconnect();
          setupRCObserver(widget);
        }
      });
      bodyObserver.observe(document.body || document.documentElement, {
        childList: true,
        subtree: true,
      });
    }

    // ── Variant data ───────────────────────────────────────────────────────

    /**
     * Read from the last [data-product-variants] script on the page so that
     * quick-add modals (which render additional product forms) don't bleed
     * into the main product form.
     */
    var variantData = (function () {
      try {
        var scripts = document.querySelectorAll(
          'script[data-product-variants]'
        );
        var el = scripts[scripts.length - 1];
        if (el) return JSON.parse(el.textContent);
      } catch (e) {
        console.warn('[ATC] Variant JSON parse error', e);
      }
      return null;
    })();

    function getVariantById(id) {
      if (!variantData) return null;
      for (var i = 0; i < variantData.length; i++) {
        if (String(variantData[i].id) === String(id)) return variantData[i];
      }
      return null;
    }

    // ── Variant change handler ─────────────────────────────────────────────

    /**
     * Triggered by a change event on input[name="id"].
     * product-info.js dispatches this inside updateVariantInputs() after
     * fetching new section HTML. Our sole job here is price display —
     * disabled state is already handled by toggleSubmitButton().
     */
    function onVariantIdChange(idInput) {
      if (!idInput || !idInput.value) return;

      var variant = getVariantById(idInput.value);

      if (!variant) {
        // Variant not in local JSON (combined listing, cross-sell, etc.).
        // Preserve the last shown price rather than going blank.
        console.warn('[ATC] Variant not found in local data:', idInput.value);
        if (lastVariantPriceCents !== null) {
          showPrice(true);
          setPrice(formatMoney(lastVariantPriceCents));
        }
        return;
      }

      if (!variant.available) {
        lastVariantPriceCents = null;
        updateButtonDisplay(true);
        return;
      }

      // 1. Show the variant base price immediately so the button is never blank.
      lastVariantPriceCents = variant.price;
      updateButtonDisplay(false);
      setPrice(formatMoney(variant.price));

      /**
       * 2. Recharge is a Vue app — it recalculates prices asynchronously after
       *    a variant change, which can take well beyond a single animation frame.
       *    Poll for up to ~1 s so we catch the update regardless of when it lands.
       *    The MutationObserver handles user-driven RC interactions separately.
       *
       *    Run the full duration (don't stop on first RC price found) because
       *    the first RC price seen after a variant change is almost always the
       *    previous variant's price. The pendingPrice guard in setPrice prevents
       *    redundant DOM writes when the price hasn't actually changed.
       */
      var rcPollCount = 0;
      var rcPollTimer = setInterval(function () {
        rcPollCount++;
        var rcPrice = getRCPrice();
        if (rcPrice) setPrice(rcPrice);
        if (rcPollCount >= 10) clearInterval(rcPollTimer);
      }, 100);
    }

    // ── Attach listener to input[name="id"] ───────────────────────────────

    function attachIdInputListener() {
      var form    = btn.closest('form');
      var idInput = form && form.querySelector('input[name="id"]');
      if (!idInput) return false;

      // Guard against double-binding if snippet is rendered more than once
      if (idInput.dataset.atcPriceBound === '1') return true;
      idInput.dataset.atcPriceBound = '1';

      idInput.addEventListener('change', function () {
        onVariantIdChange(idInput);
      });

      // Run immediately for the initially selected variant
      onVariantIdChange(idInput);
      return true;
    }

    // ── Init ───────────────────────────────────────────────────────────────

    /**
     * Show the price wrap only after the page has fully loaded (images,
     * scripts, Recharge widget). Until then the button reads "Add to Cart"
     * with no price so the user never sees a stale or flash-of-wrong price.
     */
    function revealPrice() {
      if (!priceWrapEl) return;
      // Only reveal if the current variant is available (data-show-price set).
      if (priceWrapEl.hasAttribute('data-show-price')) {
        priceWrapEl.style.display = '';
      }
    }

    if (document.readyState === 'complete') {
      revealPrice();
    } else {
      window.addEventListener('load', revealPrice, { once: true });
    }

    // Watch for Recharge widget via body MutationObserver.
    watchRCWidget();

    // Attach to variant id input (may not be in the DOM yet on slow connections)
    if (!attachIdInputListener()) {
      var idAttempts = 0;
      var idPoll = setInterval(function () {
        idAttempts++;
        if (attachIdInputListener() || idAttempts >= 40) {
          clearInterval(idPoll);
        }
      }, 100);
    }
  }
})();
